import { Component, Input, ViewChild } from '@angular/core';
import { ChartBaseComponent } from '../base/chart-base/chart-base.component';
import { Subject, takeUntil } from 'rxjs';
import { JournalChartTableData } from '../../models/fetch-journal-chart-table-data.model';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { CommonService } from '../../../../shared/services/common.service';
import { PrintService } from '../../../../shared/services/print.service';
import moment from 'moment/moment';
import {
  journalChartData,
  journalChartTableData,
} from '../../core/progress.selectors';
import {
  FetchJournalChartDataRequest,
  FetchJournalChartTableDataRequest,
} from '../../core/progress.actions';
import { JournalChartDataModel } from '../../models/fetch-journal-chart-data.model';
import { GroupedBarChartModel } from '../../../../../models/grouped-bar-chart.model';
import { GroupedBarChartTwoComponent } from '../../../../shared/components/charts/grouped-bar-chart-two/grouped-bar-chart-two.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-journaling-chart',
  templateUrl: './journaling-chart.component.html',
  styleUrls: ['./journaling-chart.component.css'],
})
export class JournalingChartComponent extends ChartBaseComponent {
  // for print
  @Input() printData: any;

  @ViewChild(GroupedBarChartTwoComponent, { static: false })
  groupedBarChartComponent: GroupedBarChartTwoComponent;
  onDestroy = new Subject<void>();
  chartData: {
    loading: boolean;
    data: GroupedBarChartModel;
  };
  chartTableData: {
    loading: boolean;
    data: JournalChartTableData;
  };
  fromDate: string;
  toDate: string;
  legendList = [
    {
      label: 'Red Light',
      color: '#E04F39',
      value: 1,
      checked: true,
    },
    {
      label: 'Physical Activity',
      color: '#279989',
      value: 2,
      checked: true,
    },
    {
      label: 'Screen Time',
      color: '#4298B5',
      value: 3,
      checked: true,
    },
  ];
  columns = [
    'week',
    'dateRange',
    //'sessions',
    'rlTotal',
    'paTotal',
    'stTotal',
    'noOfDays'
  ];

  initialLoad = true;
  additionalText = '';
  sortColumn = 'weekNumber';
  sortOrder: 'asc' | 'desc' = 'asc';
  protected readonly length = length;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private printService: PrintService,
    private datePipe: DatePipe,
  ) {
    super();
  }

  override onDateChange(data: { fromDate: string; toDate: string }) {
    this.fromDate = data.fromDate;
    this.toDate = data.toDate;

    // fetch chart details
    this.fetchChartDetails();
  }

  override onPrint(complete: () => void) {
    if (!this.chartData?.data) {
      this.commonService.notification('Nothing to print.', 'danger');
      complete();
      return;
    }
    // this.commonService.printElement('journal-chart-container');
    this.printService.open({
      component: JournalingChartComponent,
      data: {
        fromDate: this.fromDate,
        toDate: this.toDate,
        memberId: this.memberId,
        groupId: this.groupId,
        selectedLegends: this.getSelectedLegendValues(),
        userName: this.getUserName(this.profile),
        title: this.getPageTitle({
          profile: this.profile,
          fromDate: this.fromDate,
          toDate: this.toDate,
          chartName: this.chartName,
        }),
      },
      onClose: complete,
    });
  }

  ngOnInit() {
    if (this.printData) {
      this.fromDate = this.printData['fromDate'];
      this.toDate = this.printData['toDate'];
      this.memberId = this.printData['memberId'];
      this.groupId = this.printData['groupId'];
      const selectedLegends = this.printData['selectedLegends'];

      // mark check boxes
      this.legendList.forEach((item) => {
        item.checked = selectedLegends.includes(item.value);
      });
      this.setColumnConfig();

      // append date to title
      if (this.fromDate && this.toDate) {
        this.additionalText = `[${moment(this.fromDate).format(
          'MMM DD, YYYY',
        )} - ${moment(this.toDate).format('MMM DD, YYYY')}]`;
      } else if (this.fromDate) {
        this.additionalText = `[From ${moment(this.fromDate).format(
          'MMM DD, YYYY',
        )}]`;
      } else if (this.toDate) {
        this.additionalText = `[Till ${moment(this.toDate).format(
          'MMM DD, YYYY',
        )}]`;
      }
    }
    this.store
      .select(journalChartData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartData = {
          ...data,
          data: this.getChartData(data?.data),
        };

        // check is it for print
        if (!this.chartData?.loading && this.printData) {
          this.checkDataReadyForPrint();
        }
      });

    this.store
      .select(journalChartTableData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartTableData = data;
        // check is it for print
        if (!data.loading && this.printData) {
          this.checkDataReadyForPrint();
        }

        if (!data.loading && data.data?.hasNext) {
          setTimeout(() => {
            // check window height for infinite scroll
            this.commonService.checkInfiniteScrollHeightAndCallNext(
              'table-wrapper',
              () => {
                this.onScroll();
              },
            );
          }, 0);
        }
      });

    if (this.printData) {
      this.fetchChartDetails();
    }
  }

  fetchChartDetails() {
    // fetch chart data
    this.store.dispatch(
      FetchJournalChartDataRequest({
        payload: {
          ...(this.memberId ? { userId: this.memberId } : {}),
          ...(this.groupId ? { groupId: this.groupId } : {}),
          from: this.fromDate
            ? moment(this.fromDate).format('YYYY-MM-DD')
            : this.fromDate,
          to: this.toDate
            ? moment(this.toDate).format('YYYY-MM-DD')
            : this.toDate,
        },
        forPrint: !!this.printData,
      }),
    );

    // fetch chart table data
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  onScroll() {
    if (!this.chartTableData?.loading && this.chartTableData?.data?.hasNext) {
      this.fetchTableData(
        this.chartTableData?.data?.pageNumber + 1,
        this.chartTableData?.data?.limit,
      );
    }
  }

  onRoleToggle(item: any) {
    item.checked = !item.checked;

    // check roles and fire fetch
    const selectedRoles = this.legendList.filter((item) => item.checked);
    if (selectedRoles.length) {
      this.fetchChartDetails();

      // set column config
      this.setColumnConfig();
    } else {
      this.chartData = null;
      this.chartTableData = null;
    }
  }

  checkDataReadyForPrint() {
    if (this.chartData?.data && this.chartTableData?.data?.list?.length) {
      if (this.initialLoad) {
        this.initialLoad = false;
        return;
      }
      this.printService.start(this.printData?.title);
    }
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  getSelectedLegendValues() {
    return this.legendList
      .filter((item) => item.checked)
      .map((item) => item.value);
  }

  private fetchTableData(pageNumber = 1, limit = 10) {
    this.store.dispatch(
      FetchJournalChartTableDataRequest({
        payload: {
          ...(this.memberId ? { userId: this.memberId } : {}),
          ...(this.groupId ? { groupId: this.groupId } : {}),
          from: this.fromDate
            ? moment(this.fromDate).format('YYYY-MM-DD')
            : this.fromDate,
          to: this.toDate
            ? moment(this.toDate).format('YYYY-MM-DD')
            : this.toDate,
          pageNumber,
          limit,
          sortColumn: this.sortColumn,
          sortOrder: this.sortOrder,
        },
        forPrint: !!this.printData,
      }),
    );
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      // Toggle sort order
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to asc
      this.sortColumn = column;
      this.sortOrder = 'asc';
    }
    // Fetch data with new sorting
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  private setColumnConfig() {
    const selectedLegend = this.getSelectedLegendValues();
    this.columns = [
      'week',
      'dateRange',
      ...(selectedLegend.includes(1) ? ['rlTotal'] : []),
      ...(selectedLegend.includes(2) ? ['paTotal'] : []),
      ...(selectedLegend.includes(3) ? ['stTotal'] : []),
      'noOfDays',
    ];
  }

  private getChartData(
    data: JournalChartDataModel | undefined,
  ): GroupedBarChartModel | null {
    if (data) {
      const selectedLegend = this.getSelectedLegendValues();
      if (!selectedLegend.length) {
        return null;
      }

      return {
        config: {
          colors: {
            value: data.config.colors.rlColor,
            imputedValue: data.config.colors.paColor,
            targetColor: data.config.colors.stColor,
          },
          y: data.config.y,
        },
        data: data.data.map((item) => {
          return {
            title: `W${item.weekNumber}`,
            smallTitle: `${item.weekNumber}`,
            subtitle: `${this.datePipe.transform(item.dateRange.start, 'MM/dd', 'UTC')} - ${this.datePipe.transform(item.dateRange.end, 'MM/dd', 'UTC')}`,
            dateRange: item.dateRange,
            weekNumber: item.weekNumber,
            target: null,
            data: [
              ...(selectedLegend.includes(1)
                ? [
                    {
                      points: item.rlTotal,
                      recorded_on: null,
                      imputed: false,
                      color: data.config.colors.rlColor,
                    },
                  ]
                : []),
              ...(selectedLegend.includes(2)
                ? [
                    {
                      points: item.paTotal,
                      recorded_on: null,
                      imputed: false,
                      color: data.config.colors.paColor,
                    },
                  ]
                : []),
              ...(selectedLegend.includes(3)
                ? [
                    {
                      points: item.stTotal,
                      recorded_on: null,
                      imputed: false,
                      color: data.config.colors.stColor,
                    },
                  ]
                : []),
            ],
          };
        }),
      };
    }

    return null;
  }
}
